<template>
  <div v-if="usuario" class="editar-usuario-modal">
    <div class="modal-backdrop fade show" @click="cerrar"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" @click.self="cerrar">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Editar Usuario</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrar"></button>
          </div>

          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label" for="editarNombre">Nombre</label>
              <input id="editarNombre" v-model.trim="form.nombre" type="text" class="form-control" />
            </div>

            <div class="mb-3">
              <label class="form-label">Correo</label>
              <input type="email" class="form-control" :value="usuario.email" disabled />
            </div>

            <div class="mb-3">
              <label class="form-label" for="editarRol">Rol</label>
              <select id="editarRol" v-model="form.rol" class="form-select" :disabled="!!bloqueoRol">
                <option v-for="rol in rolesDisponibles" :key="rol.id" :value="String(rol.id)">{{ rol.name }}</option>
              </select>
              <small v-if="bloqueoRol" class="ayuda">{{ bloqueoRol }}</small>
            </div>

            <div class="mb-3">
              <label class="form-label" for="editarEstado">Estado</label>
              <div class="d-flex align-items-center gap-2">
                <span class="badge estado-badge" :class="usuario.status === 'active' ? 'bg-success' : 'bg-danger'">
                  {{ usuario.status === "active" ? "activo" : "inactivo" }}
                </span>
                <select id="editarEstado" v-model="form.estatus" class="form-select" :disabled="esUnoMismo">
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              <small class="ayuda">
                {{ esUnoMismo ? "No puedes desactivar tu propio usuario." : "Estado actual del usuario en el sistema" }}
              </small>
            </div>

            <hr />

            <div class="d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div>
                <small class="ayuda d-block">Creado</small>
                <strong>{{ formatoFecha(usuario.created_at) }}</strong>
              </div>
              <div v-if="bloqueoContrasena === null">
                <button type="button" class="btn btn-pill btn-contrasena" :disabled="actualizandoContrasena"
                  @click="actualizarContrasena">
                  <span v-if="actualizandoContrasena" class="spinner-border spinner-border-sm" role="status"
                    aria-hidden="true"></span>
                  <vue-feather v-else type="key" size="16" />
                  Actualizar contraseña
                </button>
              </div>
              <small v-else class="ayuda">{{ bloqueoContrasena }}</small>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-pill btn-cancelar" @click="cerrar">Cancelar</button>
            <button type="button" class="btn btn-pill btn-guardar" :disabled="guardando" @click="guardar">
              <span v-if="guardando" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";

const ROL_ADMIN = "1";
const SOLO_ADMIN = "No puedes cambiar el rol de Administrador. Solo un Administrador puede modificar este rol.";

export default {
  name: "EditarUsuarioModal",
  props: {
    usuario: { type: Object, default: null },
    roles: { type: Array, default: () => [] },
  },
  emits: ["cerrar", "guardado"],
  data() {
    return {
      form: { nombre: "", rol: "", estatus: "active" },
      guardando: false,
      actualizandoContrasena: false,
    };
  },
  computed: {
    usuarioActual() {
      return this.$store.state.autenticacion.usuarioActual;
    },
    soyAdmin() {
      return Boolean(this.usuarioActual?.esAdmin);
    },
    esUnoMismo() {
      return String(this.usuarioActual?.id) === String(this.usuario?.id);
    },
    editaAdmin() {
      return String(this.usuario?.rol) === ROL_ADMIN;
    },
    bloqueoRol() {
      if (this.esUnoMismo) return "No puedes cambiar tu propio rol.";
      if (this.editaAdmin && !this.soyAdmin) return SOLO_ADMIN;
      return null;
    },
    bloqueoContrasena() {
      if (this.esUnoMismo) return "Para cambiar tu contraseña usa Mi Perfil > Cambiar contraseña.";
      if (this.editaAdmin && !this.soyAdmin) return "Solo un Administrador puede cambiar la contraseña de un Administrador.";
      return null;
    },
    rolesDisponibles() {
      if (this.soyAdmin || this.editaAdmin) return this.roles;
      return this.roles.filter((r) => String(r.id) !== ROL_ADMIN);
    },
  },
  watch: {
    usuario: {
      handler(val) {
        if (!val) return;
        this.form = { nombre: val.name || "", rol: String(val.rol), estatus: val.status || "active" };
      },
      immediate: true,
    },
  },
  methods: {
    cerrar() {
      if (this.guardando) return;
      this.$emit("cerrar");
    },
    formatoFecha(valor) {
      if (!valor) return "—";
      const d = new Date(valor);
      if (Number.isNaN(d.getTime())) return "—";
      const dos = (n) => String(n).padStart(2, "0");
      return `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${dos(d.getHours())}:${dos(d.getMinutes())}`;
    },
    async guardar() {
      if (!this.form.nombre) {
        Swal.fire({ icon: "warning", title: "El nombre es obligatorio" });
        return;
      }
      this.guardando = true;
      try {
        await this.$store.dispatch("autenticacion/actualizarDatosUsuarioBD", {
          id: this.usuario.id,
          nombre: this.form.nombre,
          rol: this.form.rol,
          estatus: this.form.estatus,
        });
        this.guardando = false;
        this.$emit("guardado");
        Swal.fire({ icon: "success", title: "Usuario actualizado", timer: 1800, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.msg || "No se pudo actualizar el usuario." });
      } finally {
        this.guardando = false;
      }
    },
    async actualizarContrasena() {
      const row = this.usuario;
      const escapar = (texto) =>
        String(texto ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const { isConfirmed, value } = await Swal.fire({
        icon: "info",
        title: "Actualizar contraseña",
        html: `
          <p class="mb-3">Usuario: <strong>${escapar(row.name)}</strong><br><small>${escapar(row.email)}</small></p>
          <input id="nuevaContrasena" type="password" class="swal2-input" placeholder="Nueva contraseña" autocomplete="new-password">
          <input id="confirmarContrasena" type="password" class="swal2-input" placeholder="Confirmar contraseña" autocomplete="new-password">
          <p class="small text-muted mt-3 mb-0">Se cerrará la sesión del usuario en todos sus dispositivos y deberá ingresar con la nueva contraseña.</p>
        `,
        showCancelButton: true,
        confirmButtonText: "Actualizar",
        cancelButtonText: "Cancelar",
        focusConfirm: false,
        didOpen: () => document.getElementById("nuevaContrasena")?.focus(),
        preConfirm: () => {
          const nueva = document.getElementById("nuevaContrasena").value;
          const confirmar = document.getElementById("confirmarContrasena").value;
          if (nueva.length < 6) {
            Swal.showValidationMessage("La contraseña debe tener al menos 6 caracteres");
            return false;
          }
          if (nueva !== confirmar) {
            Swal.showValidationMessage("Las contraseñas no coinciden");
            return false;
          }
          return nueva;
        },
      });
      if (!isConfirmed) return;

      this.actualizandoContrasena = true;
      try {
        const data = await this.$store.dispatch("autenticacion/actualizarContrasenaUsuario", {
          id: row.id,
          contraseña: value,
        });
        Swal.fire({
          icon: "success",
          title: "Contraseña actualizada",
          text: data?.msg || "Se cerró la sesión del usuario en todos sus dispositivos.",
        });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: e.code || "Error",
          text: e.msg || "No se pudo actualizar la contraseña.",
        });
      } finally {
        this.actualizandoContrasena = false;
      }
    },
  },
};
</script>

<style scoped>
.editar-usuario-modal .modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

.editar-usuario-modal .modal {
  position: fixed;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 1055;
}

.modal-title {
  font-size: 15px;
  font-weight: 600;
}

.form-label {
  margin-bottom: 6px;
  font-size: 13px;
}

.ayuda {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #6c757d;
}

.estado-badge {
  padding: 8px 12px;
  font-size: 12px;
}

.btn-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 24px;
  border: none;
  border-radius: 30px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-pill:hover:not(:disabled) {
  color: #fff;
  transform: translateY(-1px);
}

.btn-pill:disabled {
  color: #fff;
  opacity: 0.65;
}

.btn-cancelar {
  background-color: #f5b72f;
}

.btn-cancelar:hover {
  background-color: #e6a91f;
  box-shadow: 0 6px 14px rgba(245, 183, 47, 0.3);
}

.btn-guardar,
.btn-contrasena {
  background-color: #5c61f2;
}

.btn-guardar:hover:not(:disabled),
.btn-contrasena:hover:not(:disabled) {
  background-color: #4b50e0;
  box-shadow: 0 6px 14px rgba(92, 97, 242, 0.3);
}
</style>
